import { InputAdornment, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useState } from 'react';
import type { Member } from '../actions/getMembers';
import Button from '../components/Button';
import EditMemberForm from '../components/EditMemberForm';
import MemberCard from '../components/MemberCard';
import { ReactComponent as SearchIcon } from '../icons/search.svg';
import useStore from '../store/store';

interface MembersPageProps {
    members?: Member[];
    isAdmin?: boolean;
    onEditMember?: (member: Member) => void;
    userId?: number;
    onMemberAdded?: () => void;
}

const MembersPage = ({
    members: propMembers = [],
    isAdmin = false,
    onEditMember,
    userId,
    onMemberAdded,
}: MembersPageProps) => {
    const setTitle = useStore((state) => state.setTitle);
    const storeMembers = useStore((state) => state.members);
    const members: Member[] = isAdmin ? propMembers : storeMembers;
    const isLoading = useStore((state) => state.isLoading);
    const [newMemberDialogOpen, setNewMemberDialogOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const classes = useStyles({});
    const { userPhone, userInstitution, userEmail } = useStore(
        (state) => state
    );

    const handleNewMemberClick = () => setNewMemberDialogOpen(true);
    const handleNewMemberDialogClose = () => setNewMemberDialogOpen(false);

    useEffect(() => {
        setTitle('Genome Portal - Members');
    }, [setTitle]);

    return (
        <div className={classes.root}>
            <div className={classes.topBackgroundCover}></div>
            <div className={classes.searchPanel}>
                {isAdmin && (
                    <div className={classes.userDetails}>
                        <span
                            style={{ marginRight: '16px', marginLeft: '12px' }}
                        >
                            Institution: <b>{userInstitution || 'N/A'}</b>
                        </span>
                        <span style={{ marginRight: '16px' }}>
                            Phone: <b>{userPhone || 'N/A'}</b>
                        </span>
                        <span>
                            Email: <b>{userEmail || 'N/A'}</b>
                        </span>
                    </div>
                )}
                <div className={classes.searchBarContainer}>
                    <TextField
                        id="search-bar"
                        variant="outlined"
                        className={classes.searchBar}
                        placeholder="Search Members"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                    <div className={classes.searchBarButtons}>
                        <Button
                            icon={<AddIcon />}
                            onClick={handleNewMemberClick}
                            disabled={isAdmin && !userId}
                        >
                            New Member
                        </Button>
                        <EditMemberForm
                            open={newMemberDialogOpen}
                            onClose={handleNewMemberDialogClose}
                            isAddMode
                            isAdmin={isAdmin}
                            userId={userId}
                            onMemberUpdated={onMemberAdded}
                        />
                    </div>
                </div>
            </div>
            <div className={classes.members}>
                <div className={classes.recentlyViewed}>
                    <div className={classes.widgetContainer}>
                        <div className={classes.header}>
                            <p>Members</p>
                        </div>
                        <div className={classes.content}>
                            <div className={classes.memberCardsContainer}>
                                <div className={classes.memberCards}>
                                    {isLoading
                                        ? [...Array(3)].map((_, index) => (
                                              <Skeleton
                                                  key={index}
                                                  variant="rect"
                                                  width={366}
                                                  height={240}
                                              />
                                          ))
                                        : members
                                              .filter((m) => {
                                                  const fullName =
                                                      `${m.FirstName} ${m.LastName}`.toLowerCase();
                                                  const searchWords = searchText
                                                      .toLowerCase()
                                                      .trim()
                                                      .split(/\s+/);

                                                  return searchWords.every(
                                                      (word) =>
                                                          fullName.includes(
                                                              word
                                                          ) ||
                                                          m.PatientId?.toLowerCase().includes(
                                                              word
                                                          )
                                                  );
                                              })
                                              .map((member) => (
                                                  <MemberCard
                                                      key={member.MemberId}
                                                      member={member}
                                                      isAdmin={isAdmin}
                                                      onEditMember={
                                                          onEditMember
                                                      }
                                                  />
                                              ))}
                                </div>
                            </div>
                            <div className={classes.buttons}></div>
                        </div>
                    </div>
                </div>
                {!isAdmin && (
                    <div className={classes.stats}>
                        <div className={classes.cardSimple}>
                            <div className={classes.cardSimpleLabel}>
                                Tests Completed
                            </div>
                            <div className={classes.cardSimpleValue}>
                                {isLoading
                                    ? '...'
                                    : members.reduce(
                                          (sum, m) => sum + m.ReadyKitCount,
                                          0
                                      )}
                            </div>
                        </div>
                        <div className={classes.cardSimple}>
                            <div className={classes.cardSimpleLabel}>
                                Pending Tests
                            </div>
                            <div className={classes.cardSimpleValue}>
                                {isLoading
                                    ? '...'
                                    : members.reduce(
                                          (sum, m) =>
                                              sum + m.InProgressKitCount,
                                          0
                                      )}
                            </div>
                        </div>
                        <div className={classes.cardSimple}>
                            <div className={classes.cardSimpleLabel}>
                                All Members
                            </div>
                            <div className={classes.cardSimpleValue}>
                                {isLoading ? '...' : members.length}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        padding: '40px',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '32px',
        flex: '1 0 0',
        alignSelf: 'stretch',
        background: theme.palette.primary.lighter,
    },
    searchPanel: {
        display: 'flex',
        flexDirection: 'column',
        padding: '18px 26px',
        alignItems: 'stretch',
        alignSelf: 'stretch',
        borderRadius: '12px',
        background: '#FAFCFF',
        boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.02)',
        gap: '12px',
        position: 'sticky',
        top: '105px',
        zIndex: 10,
    },
    searchBarContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '10px',
    },
    userDetails: {},
    searchBar: {
        display: 'flex',
        height: '48px',
        padding: '6px 20px',
        alignItems: 'left',
        justifyContent: 'center',
        gap: '14px',
        flex: '1 0 0',
        borderRadius: '35px',
        border: `1px solid ${theme.palette.gray.light}`,
        backgroundColor: '#FFF',

        // Override the internal input styles:
        '& .MuiOutlinedInput-root': {
            borderRadius: '35px',
            padding: 0,
            '& fieldset': {
                border: 'none',
            },
            '& input': {
                padding: 0,
                fontFamily: '"Noto Sans", sans-serif',
                fontSize: '16px',
                color: '#000',
            },
        },
    },
    searchBarButtons: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '10px',
    },
    members: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '20px',
        alignSelf: 'stretch',
        flex: 1,
    },
    recentlyViewed: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        alignSelf: 'stretch',
    },
    widgetContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        alignSelf: 'stretch',
    },
    header: {
        display: 'flex',
        height: '58px',
        padding: '10px 32px',
        justifyContent: 'space-between',
        alignItems: 'center',
        alignSelf: 'stretch',
        borderRadius: '12px 12px 0px 0px',
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.main,
        textAlign: 'center',
        fontFamily: 'Poppins',
        fontSize: '18px',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: '140%',
    },
    content: {
        display: 'flex',
        padding: 20,
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignItems: 'flex-end',
        gap: 14,
        alignSelf: 'stretch',
        borderRadius: '0px 0px 12px 12px',
        backgroundColor: '#F5F9FF',
    },
    memberCardsContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 16,
        alignSelf: 'stretch',
    },
    memberCards: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(366px, 1fr))',
        gap: 20,
        width: '100%',
    },
    stats: {
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        alignSelf: 'stretch',
    },
    cardSimple: {
        display: 'flex',
        padding: '18px',
        justifyContent: 'space-between',
        alignItems: 'center',
        flex: '1 0 0',
        borderRadius: '10px',
        background: '#FFF',
    },
    cardSimpleLabel: {
        color: theme.palette.primary.main,
        fontFamily: '"Noto Sans"',
        fontSize: '20px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '150%',
        letterSpacing: '-0.36px',
    },
    cardSimpleValue: {
        color: theme.palette.primary.main,
        fontFamily: '"Noto Sans"',
        fontSize: '24px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '150%',
        letterSpacing: '-0.432px',
    },
    buttons: {
        display: 'flex',
        padding: '4px 6px',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '10px',
    },
    topBackgroundCover: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '105px',
        background: theme.palette.primary.lighter,
        zIndex: 9,
        pointerEvents: 'none',
    },
}));

export default MembersPage;
